import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { 
  Building2, Calendar, Globe, Hash, FileText, ArrowLeft, 
  Users, Clock, AlertTriangle, BookmarkPlus, BookmarkCheck, 
  Bookmark, Code, Info, Settings, Award, CalendarClock, 
  FileEdit, ShieldCheck, Briefcase, Gavel, Search, FileSearch 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { formatDate } from "@/utils/dateFormatters";
import { useToast } from "@/hooks/use-toast";
import { TenderDataView } from "@/components/tender-view/TenderDataView";
import { TestDetailedDataTab } from "@/components/tender-view/TestDetailedDataTab";
import { TedQueryInfo } from "@/components/tender-view/TedQueryInfo";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TenderTimeline } from "@/components/tender-view/TenderTimeline";
import { TenderInformationTab } from "@/components/tender-view/TenderInformationTab";
import { DocumentsCard } from "@/components/tender-view/DocumentsCard";
import { getProcedureType, getProcedureTypeDescription, getProcedureDetailedDescription, isContractAwardNotice, getNoticeTypeDescription, getNoticeTypeCategory } from "@/utils/procedureTypeUtils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import PageLayout from "@/components/layout/PageLayout";
import { useTranslation } from "react-i18next";

type UserData = {
  id: string;
};

const TenderView = () => {
  const { tenderId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, i18n } = useTranslation(["tenderView", "nav"]);
  const [isBookmarked, setIsBookmarked] = useState<boolean>(false);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState<boolean>(true);
  const [showRawData, setShowRawData] = useState<boolean>(false);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const {
          data: {
            session
          },
          error
        } = await supabase.auth.getSession();
        if (error) {
          console.error("Error fetching user session:", error);
          setIsLoadingUser(false);
          return;
        }
        if (session?.user) {
          setUserData({
            id: session.user.id
          });
          if (tenderId) {
            const {
              data,
              error: bookmarkError
            } = await supabase.from("saved_tenders").select("*").eq("tender_id", tenderId).eq("user_id", session.user.id).maybeSingle();
            if (bookmarkError) {
              console.error("Error checking bookmark status:", bookmarkError);
            } else if (data) {
              setIsBookmarked(true);
            }
          }
        }
      } catch (error) {
        console.error("Error in fetchUserData:", error);
      } finally {
        setIsLoadingUser(false);
      }
    };
    fetchUserData();
  }, [tenderId]);

  const isValidTenderId = tenderId && tenderId !== "undefined";

  const {
    data: tender,
    isLoading,
    error
  } = useQuery({
    queryKey: ["tender-details", tenderId],
    queryFn: async () => {
      if (!isValidTenderId) {
        throw new Error("Invalid tender ID");
      }
      const publicationNumber = tenderId;
      console.log("Fetching tender details for publication number:", publicationNumber);
      try {
        const {
          data,
          error
        } = await supabase.functions.invoke("fetch-tender-details", {
          body: {
            publicationNumber
          }
        });
        if (error) {
          console.error("Error fetching tender details:", error);
          throw error;
        }
        if (!data) {
          console.error("No data returned from fetch-tender-details");
          throw new Error("No data returned from API");
        }
        console.log("Tender data received:", data);
        return filterTedLinks(data);
      } catch (err) {
        console.error("Detailed error info:", err);
        if (err.message?.includes("404") || err.message?.includes("not found")) {
          throw new Error(`Tender with publication number ${publicationNumber} not found. The tender may have expired or been removed.`);
        }
        throw err;
      }
    },
    retry: 1,
    enabled: isValidTenderId
  });

  const filterTedLinks = (tender: any) => {
    if (!tender) return null;
    const filteredTender = JSON.parse(JSON.stringify(tender));
    if (filteredTender.links) {
      if (typeof filteredTender.links === 'object' && !Array.isArray(filteredTender.links)) {
        Object.keys(filteredTender.links).forEach(key => {
          if (filteredTender.links[key] && typeof filteredTender.links[key] === 'object') {
            Object.keys(filteredTender.links[key]).forEach(lang => {
              const link = filteredTender.links[key][lang];
              if (typeof link === 'string' && (link.includes('ted.europa.eu') || link.includes('simap.ted.europa.eu'))) {
                delete filteredTender.links[key][lang];
              }
            });
          }
        });
      } else if (Array.isArray(filteredTender.links)) {
        filteredTender.links = filteredTender.links.filter((link: string) => !link.includes('ted.europa.eu') && !link.includes('simap.ted.europa.eu'));
      }
    }
    return filteredTender;
  };

  if (!isValidTenderId) {
    return (
      <PageLayout
        title={i18n.language === "da" ? "Ugyldigt udbud-ID" : "Invalid Tender ID"}
        description={i18n.language === "da" ? "Udbud-ID mangler eller er ugyldigt" : "The tender ID is missing or invalid"}
        icon={<AlertTriangle className="h-6 w-6" />}
      >
        <div className="container mx-auto py-12">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-red-700 mb-2">
              {i18n.language === "da" ? "Ugyldigt udbud-ID" : "Invalid Tender ID"}
            </h2>
            <p className="text-gray-700 mb-4">
              {i18n.language === "da" 
                ? "Udbud-ID mangler eller er ugyldigt. Gå venligst tilbage og vælg et gyldigt udbud." 
                : "The tender ID is missing or invalid. Please go back and select a valid tender."}
            </p>
            <Button onClick={() => navigate("/tender-agent")} variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" /> 
              {i18n.language === "da" ? "Tilbage til Udbudsagent" : "Back to Tender Agent"}
            </Button>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (isLoading) {
    return (
      <PageLayout
        title={i18n.language === "da" ? "Indlæser udbud" : "Loading Tender"}
        description={i18n.language === "da" 
          ? "Vent venligst mens vi indlæser udbudsdetaljerne" 
          : "Please wait while we load the tender details"}
        icon={<FileSearch className="h-6 w-6" />}
      >
        <div className="container mx-auto py-12 flex justify-center items-center min-h-[50vh]">
          <div className="text-center space-y-4">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
            <p className="text-gray-600">
              {i18n.language === "da" ? "Indlæser udbudsdetaljer..." : "Loading tender details..."}
            </p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (error || !tender) {
    return (
      <PageLayout
        title={i18n.language === "da" ? "Fejl ved indlæsning af udbud" : "Error Loading Tender"}
        description={i18n.language === "da" 
          ? "Vi kunne ikke indlæse detaljerne for dette udbud" 
          : "We couldn't load the details for this tender"}
        icon={<AlertTriangle className="h-6 w-6" />}
      >
        <div className="container mx-auto py-12">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-red-700 mb-2">Error Loading Tender</h2>
            <p className="text-gray-700 mb-4">
              We couldn't load the details for this tender. It may have expired or been removed.
            </p>
            <div className="text-sm text-red-600 mb-4 max-w-lg mx-auto overflow-auto text-left p-4 bg-red-50 border border-red-100 rounded">
              <p className="font-semibold">Error details:</p>
              <p className="whitespace-normal break-words">{error?.message || "Unknown error occurred"}</p>
            </div>
            <Button onClick={() => navigate("/tender-agent")} variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Tender Agent
            </Button>
          </div>
        </div>
      </PageLayout>
    );
  }

  const getTitle = () => {
    if (tender.TI && tender.TI.eng) {
      return tender.TI.eng;
    }
    if (tender["notice-title"]?.eng) {
      return tender["notice-title"].eng;
    }
    if (tender.match_reasons?.["notice-title"]?.eng) {
      return tender.match_reasons["notice-title"].eng;
    }
    if (tender.match_reasons?.notice_title?.eng?.[0]) {
      return tender.match_reasons.notice_title.eng[0];
    }
    if (tender["notice-title"]) {
      const availableLanguages = Object.keys(tender["notice-title"]);
      if (availableLanguages.length > 0) {
        return tender["notice-title"][availableLanguages[0]];
      }
    }
    if (tender.title) {
      return tender.title;
    }
    if (tenderId) {
      return `Tender #${tenderId.substring(0, 8)}`;
    }
    return "Untitled Tender";
  };

  const getBuyerName = () => {
    if (tender["organisation-name-buyer"]) {
      const availableLanguages = Object.keys(tender["organisation-name-buyer"]);
      if (availableLanguages.length > 0) {
        const orgName = tender["organisation-name-buyer"][availableLanguages[0]];
        if (Array.isArray(orgName) && orgName.length > 0) {
          return orgName[0];
        }
      }
    }
    if (tender["buyer-name"]?.eng?.[0]) {
      return tender["buyer-name"].eng[0];
    }
    if (tender["buyer-name"]) {
      const availableLanguages = Object.keys(tender["buyer-name"]);
      if (availableLanguages.length > 0) {
        return tender["buyer-name"][availableLanguages[0]][0];
      }
    }
    if (tender.match_reasons?.["buyer-name"]?.eng?.[0]) {
      return tender.match_reasons["buyer-name"].eng[0];
    }
    const buyerNameObj = tender.match_reasons?.["buyer-name"];
    if (buyerNameObj) {
      const firstLang = Object.values(buyerNameObj)[0];
      if (Array.isArray(firstLang) && firstLang.length > 0) {
        return firstLang[0];
      }
    }
    if (tender.organization_name) {
      return tender.organization_name;
    }
    if (tender.buyer_name) {
      return tender.buyer_name;
    }
    return "Organization not specified";
  };

  const getDescription = () => {
    let description = '';

    if (tender["BT-24-Procedure"]) {
      const availableLanguages = Object.keys(tender["BT-24-Procedure"]);
      if (availableLanguages.length > 0) {
        description = tender["BT-24-Procedure"][availableLanguages[0]];
      }
    }

    if (tender["BT-24-Lot"]) {
      const availableLanguages = Object.keys(tender["BT-24-Lot"]);
      if (availableLanguages.length > 0) {
        const lotDescription = Array.isArray(tender["BT-24-Lot"][availableLanguages[0]]) ? tender["BT-24-Lot"][availableLanguages[0]][0] : tender["BT-24-Lot"][availableLanguages[0]];
        if (description) {
          description += '\n\n--- Contract details ---\n\n' + lotDescription;
        } else {
          description = lotDescription;
        }
      }
    }

    if (!description) {
      if (tender["description-lot"]?.eng?.[0]) {
        description = tender["description-lot"].eng[0];
      } else if (tender["description-lot"]) {
        const availableLanguages = Object.keys(tender["description-lot"]);
        if (availableLanguages.length > 0) {
          description = tender["description-lot"][availableLanguages[0]][0];
        }
      } else if (tender.match_reasons?.["description-lot"]?.eng?.[0]) {
        description = tender.match_reasons["description-lot"].eng[0];
      } else {
        const descObj = tender.match_reasons?.["description-lot"];
        if (descObj) {
          const langs = Object.keys(descObj);
          if (langs.length > 0) {
            const firstLang = descObj[langs[0]];
            if (Array.isArray(firstLang) && firstLang.length > 0) {
              description = firstLang[0];
            }
          }
        } else if (tender.description) {
          description = tender.description;
        } else {
          description = "No description available";
        }
      }
    }
    return description;
  };

  const getLocation = () => {
    if (tender["place-of-performance-country-lot"]?.[0]) {
      return tender["place-of-performance-country-lot"][0];
    }
    if (tender.match_reasons?.["place-of-performance-country-lot"]?.[0]) {
      return tender.match_reasons["place-of-performance-country-lot"][0];
    }
    if (tender.location) {
      return tender.location;
    }
    if (tender.country) {
      return tender.country;
    }
    return "Location not specified";
  };

  // Updated getDeadline function to be consistent with dashboard
  const getDeadline = () => {
    // Helper function to try parsing and formatting a date from multiple formats
    const tryFormatDate = (dateStr: any): string | null => {
      if (!dateStr) return null;
      try {
        return formatDate(dateStr);
      } catch (e) {
        console.warn("Failed to format date:", dateStr, e);
        return null;
      }
    };

    // For saved tenders with tender_data structure
    if (tender?.tender_data) {
      // Try most common TED API deadline fields
      if (tender.tender_data["deadline-receipt-tender-date-lot"]) {
        const dateStr = Array.isArray(tender.tender_data["deadline-receipt-tender-date-lot"]) 
          ? tender.tender_data["deadline-receipt-tender-date-lot"][0] 
          : tender.tender_data["deadline-receipt-tender-date-lot"];
        const formatted = tryFormatDate(dateStr);
        if (formatted) return formatted;
      }

      if (tender.tender_data["deadline-receipt-request"]) {
        const dateStr = Array.isArray(tender.tender_data["deadline-receipt-request"]) 
          ? tender.tender_data["deadline-receipt-request"][0] 
          : tender.tender_data["deadline-receipt-request"];
        const formatted = tryFormatDate(dateStr);
        if (formatted) return formatted;
      }

      // Try deadline_date (often our standardized field)
      if (tender.tender_data.deadline_date) {
        const formatted = tryFormatDate(tender.tender_data.deadline_date);
        if (formatted) return formatted;
      }

      // Try match_reasons which might contain deadline info
      if (tender.tender_data.match_reasons?.["deadline-receipt-tender-date-lot"]) {
        const dateArr = tender.tender_data.match_reasons["deadline-receipt-tender-date-lot"];
        if (Array.isArray(dateArr) && dateArr.length > 0) {
          const formatted = tryFormatDate(dateArr[0]);
          if (formatted) return formatted;
        }
      }
      
      // Try DT field which is sometimes used
      if (tender.tender_data.DT) {
        const formatted = tryFormatDate(tender.tender_data.DT);
        if (formatted) return formatted;
      }
    }

    // For direct tender structure (not within tender_data)
    if (tender["deadline-receipt-request"]) {
      const dateStr = Array.isArray(tender["deadline-receipt-request"]) 
        ? tender["deadline-receipt-request"][0] 
        : tender["deadline-receipt-request"];
      const formatted = tryFormatDate(dateStr);
      if (formatted) return formatted;
    }
    
    if (tender["deadline-receipt-tender-date-lot"]) {
      const dateStr = Array.isArray(tender["deadline-receipt-tender-date-lot"]) 
        ? tender["deadline-receipt-tender-date-lot"][0] 
        : tender["deadline-receipt-tender-date-lot"];
      const formatted = tryFormatDate(dateStr);
      if (formatted) return formatted;
    }

    if (tender.match_reasons?.["deadline-receipt-tender-date-lot"]) {
      const dateArr = tender.match_reasons["deadline-receipt-tender-date-lot"];
      if (Array.isArray(dateArr) && dateArr.length > 0) {
        const formatted = tryFormatDate(dateArr[0]);
        if (formatted) return formatted;
      }
    }
    
    // Try standard fields
    if (tender.deadline_date) {
      const formatted = tryFormatDate(tender.deadline_date);
      if (formatted) return formatted;
    }
    
    if (tender.deadline) {
      const formatted = tryFormatDate(tender.deadline);
      if (formatted) return formatted;
    }
    
    // Try the DT field which is sometimes used
    if (tender.DT) {
      const formatted = tryFormatDate(tender.DT);
      if (formatted) return formatted;
    }
    
    return i18n.language === "da" ? "Deadline ikke angivet" : "Deadline not specified";
  };

  const getPublicationNumber = () => {
    if (tender["publication-number"]) {
      return tender["publication-number"];
    }
    if (tender.match_reasons?.["publication-number"]) {
      return tender.match_reasons["publication-number"];
    }
    if (tender.publication_number) {
      return tender.publication_number;
    }
    return "Not specified";
  };

  const getCpvCodes = () => {
    if (Array.isArray(tender["classification-cpv"]) && tender["classification-cpv"].length > 0) {
      return tender["classification-cpv"];
    }
    if (Array.isArray(tender.match_reasons?.["classification-cpv"]) && tender.match_reasons["classification-cpv"].length > 0) {
      return tender.match_reasons["classification-cpv"];
    }
    if (Array.isArray(tender.cpv_codes) && tender.cpv_codes.length > 0) {
      return tender.cpv_codes;
    }
    return [];
  };

  const getTedUrl = () => {
    return null;
  };

  const getValue = () => {
    if (tender["estimated-value-proc"]) {
      const value = tender["estimated-value-proc"];
      const currency = tender["estimated-value-cur-proc"] || "EUR";
      return `${value} ${currency}`;
    }
    if (tender["tender-value"]?.value) {
      return `${tender["tender-value"].value} ${tender["tender-value"].currency || "EUR"}`;
    }
    if (tender.match_reasons?.["tender-value"]?.value) {
      return `${tender.match_reasons["tender-value"].value} ${tender.match_reasons["tender-value"].currency || "EUR"}`;
    }
    if (tender.value) {
      return tender.value;
    }
    return "Value not specified";
  };

  const getNoticeTypeIcon = (noticeType: string | null, category: string | null) => {
    if (!noticeType) return <FileText className="h-4 w-4" />;
    switch (category) {
      case 'award':
        return <Award className="h-4 w-4" />;
      case 'contract':
        return <FileText className="h-4 w-4" />;
      case 'prior':
        return <CalendarClock className="h-4 w-4" />;
      case 'change':
        return <FileEdit className="h-4 w-4" />;
      default:
        if (noticeType === 'veat') return <ShieldCheck className="h-4 w-4" />;
        if (noticeType === 'subco') return <Briefcase className="h-4 w-4" />;
        if (noticeType === 'cn-desg' || noticeType === 'can-desg') return <Gavel className="h-4 w-4" />;
        return <FileText className="h-4 w-4" />;
    }
  };

  const handleSaveTender = async () => {
    if (!userData || !userData.id) {
      toast({
        title: "Authentication Required",
        description: "You must be logged in to bookmark tenders",
        variant: "destructive"
      });
      return;
    }
    if (!tenderId) {
      toast({
        title: "Error",
        description: "Invalid tender ID",
        variant: "destructive"
      });
      return;
    }
    try {
      if (isBookmarked) {
        const {
          error
        } = await supabase.from("saved_tenders").delete().eq("tender_id", tenderId).eq("user_id", userData.id);
        if (error) {
          console.error("Error removing bookmark:", error);
          toast({
            title: "Error",
            description: "Failed to remove tender from bookmarks: " + error.message,
            variant: "destructive"
          });
          return;
        }
        setIsBookmarked(false);
        toast({
          title: t("tenderView.bookmarks.success"),
          description: t("tenderView.bookmarks.removed")
        });
      } else {
        const tenderDataToSave = JSON.parse(JSON.stringify(tender));

        const enhancedTenderData = {
          ...tenderDataToSave,
          title: getTitle(),
          organization_name: getBuyerName(),
          value: getValue(),
          deadline_date: getDeadline(),
          publication_number: getPublicationNumber()
        };
        console.log("Saving tender with enhanced metadata:", {
          title: enhancedTenderData.title,
          organization: enhancedTenderData.organization_name,
          value: enhancedTenderData.value
        });
        
        const {
          error
        } = await supabase.from("saved_tenders").insert({
          user_id: userData.id,
          tender_data: enhancedTenderData,
          status: "not_started"
        });
        
        if (error) {
          console.error("Error saving bookmark:", error);
          toast({
            title: "Error",
            description: "Failed to bookmark tender: " + error.message,
            variant: "destructive"
          });
          return;
        }
        setIsBookmarked(true);
        toast({
          title: t("tenderView.bookmarks.success"),
          description: t("tenderView.bookmarks.bookmarked")
        });
      }
    } catch (error) {
      console.error("Unexpected error in handleSaveTender:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive"
      });
    }
  };

  // Updated calculateDaysRemaining function to match the improved deadline detection
  const calculateDaysRemaining = () => {
    // Helper function to try parsing a date from multiple formats
    const tryParseDate = (dateStr: any): Date | null => {
      if (!dateStr) return null;
      try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return null;
        return date;
      } catch (e) {
        console.warn("Failed to parse date:", dateStr, e);
        return null;
      }
    };

    // For saved tenders with tender_data structure
    let deadlineDate = null;
    
    if (tender?.tender_data) {
      // Try most common TED API deadline fields
      if (tender.tender_data["deadline-receipt-tender-date-lot"]) {
        const dateStr = Array.isArray(tender.tender_data["deadline-receipt-tender-date-lot"]) 
          ? tender.tender_data["deadline-receipt-tender-date-lot"][0] 
          : tender.tender_data["deadline-receipt-tender-date-lot"];
        deadlineDate = tryParseDate(dateStr);
        if (deadlineDate) return Math.ceil((deadlineDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
      }

      // Try match_reasons which might contain deadline info
      if (!deadlineDate && tender.tender_data.match_reasons?.["deadline-receipt-tender-date-lot"]) {
        const dateArr = tender.tender_data.match_reasons["deadline-receipt-tender-date-lot"];
        if (Array.isArray(dateArr) && dateArr.length > 0) {
          deadlineDate = tryParseDate(dateArr[0]);
          if (deadlineDate) return Math.ceil((deadlineDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
        }
      }

      // Try standard fields
      if (!deadlineDate && tender.tender_data.deadline_date) {
        deadlineDate = tryParseDate(tender.tender_data.deadline_date);
        if (deadlineDate) return Math.ceil((deadlineDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
      }

      // Try DT field
      if (!deadlineDate && tender.tender_data.DT) {
        deadlineDate = tryParseDate(tender.tender_data.DT);
        if (deadlineDate) return Math.ceil((deadlineDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
      }
    }

    // For direct tender structure
    if (!deadlineDate && tender["deadline-receipt-request"]) {
      const dateStr = Array.isArray(tender["deadline-receipt-request"]) 
        ? tender["deadline-receipt-request"][0] 
        : tender["deadline-receipt-request"];
      deadlineDate = tryParseDate(dateStr);
      if (deadlineDate) return Math.ceil((deadlineDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    }

    if (!deadlineDate && tender["deadline-receipt-tender-date-lot"]) {
      const dateStr = Array.isArray(tender["deadline-receipt-tender-date-lot"]) 
        ? tender["deadline-receipt-tender-date-lot"][0] 
        : tender["deadline-receipt-tender-date-lot"];
      deadlineDate = tryParseDate(dateStr);
      if (deadlineDate) return Math.ceil((deadlineDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    }

    // Try standard fields
    if (!deadlineDate && tender.deadline_date) {
      deadlineDate = tryParseDate(tender.deadline_date);
      if (deadlineDate) return Math.ceil((deadlineDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    }

    if (!deadlineDate && tender.deadline) {
      deadlineDate = tryParseDate(tender.deadline);
      if (deadlineDate) return Math.ceil((deadlineDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    }
    
    // Try the DT field
    if (!deadlineDate && tender.DT) {
      deadlineDate = tryParseDate(tender.DT);
      if (deadlineDate) return Math.ceil((deadlineDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    }
    
    return null;
  };

  const daysRemaining = calculateDaysRemaining();
  const noticeType = tender["notice-type"];
  const noticeTypeDescription = noticeType ? getNoticeTypeDescription(noticeType) : null;
  const noticeTypeCategory = noticeType ? getNoticeTypeCategory(noticeType) : null;
  const tenderTitle = getTitle();

  return (
    <PageLayout
      title={tenderTitle.length > 60 ? `${tenderTitle.substring(0, 60)}...` : tenderTitle}
      description={`${i18n.language === "da" ? "Udgivet af" : "Published by"} ${getBuyerName()}`}
      icon={<FileText className="h-6 w-6" />}
    >
      <div className="container mx-auto py-8 space-y-8">
        <div className="flex justify-between items-center">
          <Button 
            variant="ghost" 
            className="flex items-center gap-2" 
            onClick={() => navigate("/tender-agent")}
          >
            <ArrowLeft className="h-4 w-4" />
            {t("actions.backToTenders", { ns: "nav" })}
          </Button>
          
          <div className="flex gap-2">
            {isBookmarked && <Button onClick={() => navigate(`/offer-management/${tenderId}`)}>
                {t("tenderView.actions.workOnTender")}
              </Button>}
          </div>
        </div>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="overview">{t("tenderView.tabs.overview")}</TabsTrigger>
            <TabsTrigger value="information">{t("tenderView.tabs.information")}</TabsTrigger>
            <TabsTrigger value="timeline">{t("tenderView.tabs.timeline")}</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-2">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <Card>
                  <CardContent className="p-6 space-y-6">
                    <div>
                      <h2 className="text-xl font-semibold mb-4">{t("tenderView.labels.description")}</h2>
                      <p className="text-gray-700 whitespace-pre-wrap">{getDescription()}</p>
                    </div>
                    
                    <Separator />
                    
                    <div>
                      <h2 className="text-xl font-semibold mb-4">{t("tenderView.labels.details")}</h2>
                      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <dt className="text-sm text-gray-500">{t("tenderView.labels.publicationNumber")}</dt>
                          <dd className="font-medium flex items-center gap-2">
                            <Hash className="h-4 w-4 text-gray-500" />
                            {getPublicationNumber()}
                          </dd>
                        </div>
                        
                        <div className="space-y-1">
                          <dt className="text-sm text-gray-500">{t("tenderView.labels.deadline")}</dt>
                          <dd className="font-medium flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-gray-500" />
                            {getDeadline()}
                          </dd>
                        </div>
                        
                        <div className="space-y-1">
                          <dt className="text-sm text-gray-500">{t("tenderView.labels.location")}</dt>
                          <dd className="font-medium flex items-center gap-2">
                            <Globe className="h-4 w-4 text-gray-500" />
                            {getLocation()}
                          </dd>
                        </div>
                        
                        <div className="space-y-1">
                          <dt className="text-sm text-gray-500">{t("tenderView.labels.value")}</dt>
                          <dd className="font-medium">{getValue()}</dd>
                        </div>
                      </dl>
                    </div>
                    
                    <Separator />
                    
                    <div>
                      <h2 className="text-xl font-semibold mb-4">{t("tenderView.labels.cpvClassifications")}</h2>
                      <div className="flex flex-wrap gap-2">
                        {getCpvCodes().length > 0 ? getCpvCodes().map((code, index) => <Badge key={`${code}-${index}`} variant="outline" className="text-sm">
                              <FileText className="h-3 w-3 mr-1" />
                              {code}
                            </Badge>) : <span className="text-gray-500 text-sm">{t("tenderView.labels.noCpvCodes")}</span>}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-6">
                <Card>
                  <CardContent className="p-6 space-y-6">
                    <h2 className="text-xl font-semibold">
                      {i18n.language === "da" ? "Handlinger" : "Actions"}
                    </h2>
                    
                    <div className="space-y-4">
                      {!isBookmarked ? <Button className="w-full flex items-center justify-center gap-2" onClick={handleSaveTender}>
                          <BookmarkPlus className="h-4 w-4" />
                          {t("tenderView.actions.save")}
                        </Button> : <>
                          <Button variant="outline" className="w-full flex items-center justify-center gap-2" onClick={handleSaveTender}>
                            <BookmarkCheck className="h-4 w-4" />
                            {i18n.language === "da" ? "Fjern fra gemte" : "Remove from Saved"}
                          </Button>
                          <Button variant="default" className="w-full flex items-center justify-center gap-2" onClick={() => navigate(`/offer-management/${tenderId}`)}>
                            <Users className="h-4 w-4" />
                            {t("tenderView.actions.workOnTender")}
                          </Button>
                        </>}
                    </div>
                    
                    <Separator />
                    
                    <div className="space-y-2">
                      <h3 className="font-medium">{t("tenderView.actions.needHelp")}</h3>
                      <p className="text-sm text-gray-600">
                        {t("tenderView.actions.helpText")}
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium">{i18n.language === "da" ? "Deadline Nedtælling" : "Deadline Countdown"}</h3>
                      <Calendar className="h-4 w-4 text-gray-500" />
                    </div>
                    
                    {daysRemaining !== null ? <>
                        <div className="text-center">
                          <span className={`text-4xl font-bold ${daysRemaining < 7 ? 'text-red-600' : 'text-primary'}`}>
                            {daysRemaining}
                          </span>
                          <p className="text-sm text-gray-600">
                            {i18n.language === "da" ? "dage tilbage" : "days left"}
                          </p>
                        </div>
                        
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div className={`h-2.5 rounded-full ${daysRemaining < 7 ? 'bg-red-600' : 'bg-primary'}`} style={{
                        width: `${Math.max(5, Math.min(100, daysRemaining / 30 * 100))}%`
                      }}></div>
                        </div>
                      </> : <p className="text-center text-gray-600">
                        {i18n.language === "da" ? "Ingen tilbudsfrist" : "No deadline information"}
                      </p>}
                  </CardContent>
                </Card>
                
                <DocumentsCard tender={tender} />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="information" className="mt-2">
            <TenderInformationTab tender={tender} />
          </TabsContent>

          <TabsContent value="timeline" className="mt-2">
            <TenderTimeline tender={tender} publicationNumber={getPublicationNumber()} />
          </TabsContent>
        </Tabs>
      </div>
    </PageLayout>
  );
};

export default TenderView;
